import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { mdiPlus } from '@mdi/js';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { BasePage } from 'hydra/pages/BasePage';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { useRouteQuery } from 'route/hooks/useRouteQuery';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { getUrl } from 'route/utils/getUrl';
import { RouteLink } from 'route/components/RouteLink';
import { Icon } from 'common/components/Icon';
import { Tab, Tabs } from 'common/components/Tabs';
import { NoResults } from 'common/components/NoResults';
import { BoxLoader } from 'common/components/Loader';
import { TrainingManagementCardList } from 'training/components/TrainingManagementCardList';
import { TrainingsManagementTable } from 'training/components/TrainingsManagementTable';
import { useTrainingList } from 'training/hooks/useTrainingList';
import { FilterBar } from 'common/components/FilterBar';
import { Pagination } from 'common/components/Pagination';
import { BackgroundImg } from 'common/components/Img';
import MANAGEMENT_HEADER from 'assets/images/training-overview-header.webp';
import { ActionButton } from 'common/components/ActionButton';
import { PageTitle } from 'common/components/PageTitle';
import { EView, ViewToggle } from 'common/components/ViewToggle';

enum ETrainingStatus {
    ACTIVE = 'active',
    ARCHIVED = 'archived',
}

export const TrainingsManagementPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const routeQuery = useRouteQuery();
    const [translate] = useTranslation();

    const { canCreate = false } = useAppPermissions('training', 'management');

    const isActivePage = !!useRouteMatch('TRAINING_MANAGEMENT_LIST');
    const trainingStatus = isActivePage
        ? ETrainingStatus.ACTIVE
        : ETrainingStatus.ARCHIVED;
    const isArchiveView = trainingStatus === ETrainingStatus.ARCHIVED;

    const localStorageView = localStorage.getItem(
        'trainingsManagementPageView'
    );
    const currentRouteView = routeQuery.get('view');
    const view =
        (currentRouteView as EView) ||
        (localStorageView as EView) ||
        EView.List;

    const navigateToView = useCallback(
        (newView: EView) => {
            const newSearchParams = new URLSearchParams(window.location.search);

            if (newSearchParams.get('view') === newView) return;

            newSearchParams.set('view', newView);

            navigate(
                { ...location, search: `?${newSearchParams.toString()}` },
                { replace: true }
            );
        },
        [location, navigate]
    );

    const setView = useCallback(
        (newView: EView) => {
            localStorage.setItem('trainingsManagementPageView', newView);

            navigateToView(newView);
        },
        [navigateToView]
    );

    useEffect(() => {
        if (!!localStorageView && !currentRouteView) {
            navigateToView(localStorageView as EView);
        }
    }, [localStorageView, currentRouteView, navigateToView]);

    const {
        trainings,
        loading,
        paginationSettings,
        filters,
        filterValues,
        setPage,
        onSearch,
        onFilterChange,
        onSort,
        currentSort,
    } = useTrainingList(undefined, true, isArchiveView);

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
    };

    const noResults = !loading && !trainings.length;

    const header = (
        <BackgroundImg
            src={MANAGEMENT_HEADER}
            sx={{
                height: '10vw',
                maxHeight: '150px',
                backgroundSize: 'contain',
                backgroundPosition: 'right top',
                display: { xs: 'none', sm: 'block' },
            }}
        />
    );

    return (
        <BasePage contentContainerMaxWidth="lg" header={header}>
            <PageTitle>
                {translate('trainingsManagementPage.pageTitle')}
            </PageTitle>
            <Box sx={{ pt: { xs: 4, sm: 2 } }}>
                <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                    <Grid size="grow">
                        <Typography variant="h1">
                            {translate('trainingsManagementPage.pageTitle')}
                        </Typography>
                    </Grid>

                    {canCreate && (
                        <Grid size="auto">
                            <ActionButton
                                outlined
                                disabled={loading}
                                variant="extended"
                                onClick={() => {
                                    navigate(getUrl('TRAINING_CREATE'));
                                }}
                            >
                                <Box>
                                    <Icon path={mdiPlus} size="2.4rem" />
                                </Box>

                                {translate('newTraining')}
                            </ActionButton>
                        </Grid>
                    )}
                </Grid>
            </Box>
            <Tabs sx={{ mt: 2 }} value={trainingStatus}>
                <Tab
                    component={RouteLink}
                    label={translate('trainingsManagementPage.tabs.active')}
                    to="TRAINING_MANAGEMENT_LIST"
                    value={ETrainingStatus.ACTIVE}
                />
                <Tab
                    component={RouteLink}
                    label={translate('trainingsManagementPage.tabs.archived')}
                    to="TRAINING_MANAGEMENT_LIST_ARCHIVED"
                    value={ETrainingStatus.ARCHIVED}
                />
            </Tabs>
            <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 4 }}>
                    <FilterBar
                        useFilterDrawer
                        barPrepend={
                            <ViewToggle value={view} onClickButton={setView} />
                        }
                        disabled={loading}
                        filterDrawerProps={{
                            title: translate('filterForm.trainings.title'),
                            description: translate(
                                'filterForm.trainings.description'
                            ),
                        }}
                        formFilters={filters}
                        formFilterValues={filterValues}
                        initialSearchValue={filterValues.q}
                        placeholder={translate('filterBarPlaceholder')}
                        onFiltersChange={(values) => {
                            setPage(1);

                            onFilterChange?.(values);
                        }}
                        onSearch={handleSearch}
                        onSearchClear={() => handleSearch('')}
                    />
                </Box>

                {noResults && <NoResults variant="TRAININGS_MANAGEMENT" />}

                {loading && !trainings.length && <BoxLoader />}

                {!loading && !noResults && (
                    <>
                        <Box sx={{ mb: 4 }}>
                            {view === EView.Grid ? (
                                <TrainingManagementCardList
                                    trainings={trainings}
                                />
                            ) : (
                                <TrainingsManagementTable
                                    archived={isArchiveView}
                                    currentSort={currentSort}
                                    trainings={trainings}
                                    onSort={onSort}
                                />
                            )}
                        </Box>
                        <Box sx={{ mt: 4 }}>
                            <Pagination
                                page={paginationSettings.page}
                                pageAmount={paginationSettings.pageAmount}
                                totalsAmount={paginationSettings.count}
                                totalsText={translate('training', {
                                    count: paginationSettings.count,
                                })}
                                onChange={(page: number) => {
                                    setPage(page);
                                }}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </BasePage>
    );
};
